import { supabase, SB, deleteUserPhotosFromStorage } from './supabase';

/*
 * HYBRID DATA SERVICE
 * localStorage = sync cache for instant UI
 * Supabase    = persistent backend, synced on every write
 *
 * Pattern: every write → update localStorage first (instant) → push to Supabase (background)
 * On startup: pull user's data from Supabase → populate localStorage cache
 */

const DB_KEY = 'jiv_db';
const SESSION_KEY = 'jiv_session';

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

function getDB() {
  const s = localStorage.getItem(DB_KEY);
  if (s) return JSON.parse(s);
  const db = createInitialDB();
  localStorage.setItem(DB_KEY, JSON.stringify(db));
  return db;
}
function saveDB(db) {
  try {
    localStorage.setItem(DB_KEY, JSON.stringify(db));
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      // Strip data URLs and retry — they can be re-fetched from Supabase
      console.warn('localStorage quota exceeded, stripping photo data URLs');
      const cleaned = { ...db };
      cleaned.photos = (cleaned.photos || []).map(p => ({
        ...p,
        url: (p.url && p.url.startsWith('data:')) ? '' : p.url,
      }));
      try { localStorage.setItem(DB_KEY, JSON.stringify(cleaned)); } catch (_) {
        localStorage.removeItem(DB_KEY);
      }
    }
  }
}

// ── Supabase background sync helper ──
function sbPush(table, op, data, match) {
  if (!SB) return;
  let q;
  if (op === 'upsert')  q = supabase.from(table).upsert(data);
  if (op === 'insert')  q = supabase.from(table).insert(data);
  if (op === 'update')  q = supabase.from(table).update(data).match(match);
  if (op === 'delete')  q = supabase.from(table).delete().match(match);
  q?.then(({ error }) => { if (error) console.warn(`[sb:${table}.${op}]`, error.message); });
}

// ── Seed data (fallback when Supabase is empty or not configured) ──
const SEED_PHOTOS = {
  priya: 'https://images.unsplash.com/photo-1771958062747-198a5e04febc?w=600&h=800&fit=crop&q=80',
  ananya: 'https://images.unsplash.com/photo-1758985402508-2a371f98de54?w=600&h=800&fit=crop&q=80',
  arjun: 'https://images.unsplash.com/photo-1762709412730-321a0d81b517?w=600&h=800&fit=crop&q=80',
  vivaan: 'https://images.unsplash.com/photo-1762708547805-472836585b39?w=600&h=800&fit=crop&q=80',
};

function createInitialDB() {
  return {
    users: [], profiles: [],
    photos: [], interactions: [], matches: [],
    messages: [], reports: [], jai_jinendra_usage: [],
    vouches: [], notifications: [],
  };
}

// ── Startup sync: pull data from Supabase into localStorage ──
export async function syncFromSupabase(userId) {
  if (!SB) return;
  try {
    const db = getDB();
    // Pull own profile
    const { data: profile } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
    if (profile) {
      const idx = db.profiles.findIndex(p => p.id === userId);
      if (idx >= 0) db.profiles[idx] = profile; else db.profiles.push(profile);
    }
    // Pull own photos
    const { data: photos } = await supabase.from('photos').select('*').eq('profile_id', userId).order('position');
    if (photos) { db.photos = db.photos.filter(p => p.profile_id !== userId).concat(photos); }
    // Pull matches
    const { data: m1 } = await supabase.from('matches').select('*').eq('user_1', userId);
    const { data: m2 } = await supabase.from('matches').select('*').eq('user_2', userId);
    const allMatches = [...(m1 || []), ...(m2 || [])];
    allMatches.forEach(m => { if (!db.matches.find(x => x.id === m.id)) db.matches.push(m); });
    // Pull interactions (own)
    const { data: ints } = await supabase.from('interactions').select('*').eq('from_user', userId);
    if (ints) ints.forEach(i => { if (!db.interactions.find(x => x.id === i.id)) db.interactions.push(i); });
    // Pull jai jinendra incoming
    const { data: jjIn } = await supabase.from('interactions').select('*').eq('to_user', userId).eq('type', 'jai_jinendra');
    if (jjIn) jjIn.forEach(i => { if (!db.interactions.find(x => x.id === i.id)) db.interactions.push(i); });
    // Pull jai_jinendra_usage for current user
    const { data: jjUsage } = await supabase.from('jai_jinendra_usage').select('*').eq('user_id', userId);
    if (jjUsage) {
      db.jai_jinendra_usage = db.jai_jinendra_usage.filter(j => j.user_id !== userId);
      jjUsage.forEach(j => { if (!db.jai_jinendra_usage.find(x => x.id === j.id)) db.jai_jinendra_usage.push(j); });
    }
    // Pull match intents for all matches
    const matchIds = allMatches.map(m => m.id);
    if (matchIds.length > 0) {
      try {
        const { data: mis } = await supabase.from('match_intent').select('*').in('match_id', matchIds);
        if (mis) {
          if (!db.match_intents) db.match_intents = [];
          mis.forEach(mi => {
            const i = db.match_intents.findIndex(x => x.match_id === mi.match_id && x.user_id === mi.user_id);
            if (i >= 0) db.match_intents[i] = mi; else db.match_intents.push(mi);
          });
        }
      } catch (_) {}
    }
    saveDB(db);
  } catch (e) { console.warn('Supabase sync error:', e); }
}

// ── Message sync: fetch messages for all of user's matches ──
export async function syncMessagesForUser(userId) {
  if (!SB) return;
  try {
    const db = getDB();
    const ownMatches = db.matches.filter(m => m.user_1 === userId || m.user_2 === userId);
    const matchIds = ownMatches.map(m => m.id);
    if (matchIds.length === 0) return;

    const { data: msgs } = await supabase
      .from('messages')
      .select('*')
      .in('match_id', matchIds)
      .order('created_at', { ascending: true });

    if (msgs && msgs.length > 0) {
      msgs.forEach(msg => {
        const i = db.messages.findIndex(m => m.id === msg.id);
        if (i >= 0) db.messages[i] = msg;
        else db.messages.push(msg);
      });
      saveDB(db);
    }
  } catch (e) {
    console.warn('Message sync error:', e);
  }
}

// ── Discovery feed: fetch other profiles from Supabase ──
export async function syncDiscoveryProfiles(userId, seeking, city) {
  if (!SB) return;
  try {
    const genderFilter = seeking === 'Everyone' ? null : seeking === 'Men' ? 'Man' : 'Woman';
    let query = supabase.from('profiles').select('*').neq('id', userId).eq('selfie_verified', true).eq('report_flag', false).eq('admin_blocked', false).neq('profile_paused', true);
    if (genderFilter) query = query.eq('gender', genderFilter);
    const { data: profiles } = await query.order('created_at', { ascending: false }).limit(50);
    if (!profiles || profiles.length === 0) return;
    const db = getDB();
    profiles.forEach(p => {
      const idx = db.profiles.findIndex(x => x.id === p.id);
      if (idx >= 0) db.profiles[idx] = p; else db.profiles.push(p);
    });
    // Fetch photos for these profiles
    const ids = profiles.map(p => p.id);
    const { data: photos } = await supabase.from('photos').select('*').in('profile_id', ids).order('position');
    if (photos) {
      photos.forEach(ph => { if (!db.photos.find(x => x.id === ph.id)) db.photos.push(ph); });
    }
    saveDB(db);
  } catch (e) { console.warn('Discovery sync error:', e); }
}

// ══════════════════════════════════════
//  SESSION
// ══════════════════════════════════════
export function getSession() { const s = localStorage.getItem(SESSION_KEY); return s ? JSON.parse(s) : null; }
export function setSession(s) { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }
export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(DB_KEY);
  if (SB) supabase.auth.signOut().catch(() => {});
}

// ══════════════════════════════════════
//  USERS (localStorage only – Supabase auth handles real users)
// ══════════════════════════════════════
export function getUserByPhone(phone) { return getDB().users.find(u => u.phone === phone) || null; }
export function createUser(phone) {
  const db = getDB();
  const user = { id: generateId(), phone, created_at: new Date().toISOString() };
  db.users.push(user); saveDB(db); return user;
}

// ══════════════════════════════════════
//  PROFILES
// ══════════════════════════════════════
export function getProfile(userId) {
  const p = getDB().profiles.find(p => p.id === userId);
  if (!p) return null;
  return {
    ...p,
    personality_traits: p.personality_traits || [],
    hobbies_list: p.hobbies_list || [],
  };
}

export function hasCompletedOnboarding(profile) {
  if (!profile) return false;
  return Boolean(
    profile.prompt_1_q && profile.prompt_1_a &&
    profile.prompt_2_q && profile.prompt_2_a &&
    profile.prompt_3_q && profile.prompt_3_a
  );
}

export function createProfile(data) {
  const db = getDB();
  const i = db.profiles.findIndex(p => p.id === data.id);
  if (i >= 0) db.profiles[i] = { ...db.profiles[i], ...data };
  else db.profiles.push(data);
  saveDB(db);
  sbPush('profiles', 'upsert', data);
  return data;
}

export function updateProfile(userId, updates) {
  const db = getDB();
  const i = db.profiles.findIndex(p => p.id === userId);
  if (i >= 0) {
    db.profiles[i] = { ...db.profiles[i], ...updates }; saveDB(db);
    sbPush('profiles', 'update', updates, { id: userId });
    return db.profiles[i];
  }
  return null;
}

// ══════════════════════════════════════
//  PHOTOS
// ══════════════════════════════════════
export function getPhotos(profileId) {
  return getDB().photos.filter(p => p.profile_id === profileId).sort((a, b) => a.position - b.position);
}

export function addPhoto(profileId, url, position, caption = '') {
  const db = getDB();
  const photo = { id: generateId(), profile_id: profileId, url, position, caption, created_at: new Date().toISOString() };
  db.photos.push(photo); saveDB(db);
  // Only push to Supabase if URL is a real URL (not a data: or blob: URL)
  if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
    sbPush('photos', 'insert', photo);
  }
}

export function removePhoto(photoId) {
  const db = getDB();
  db.photos = db.photos.filter(p => p.id !== photoId); saveDB(db);
  sbPush('photos', 'delete', null, { id: photoId });
}

export function updatePhotos(profileId, photos) {
  const db = getDB();
  const oldPhotos = db.photos.filter(p => p.profile_id === profileId);
  db.photos = db.photos.filter(p => p.profile_id !== profileId);
  // Delete old from Supabase
  oldPhotos.forEach(p => sbPush('photos', 'delete', null, { id: p.id }));
  // Insert new
  photos.forEach((p, i) => {
    const row = { id: generateId(), profile_id: profileId, url: p.url, position: i, caption: p.caption || '', created_at: new Date().toISOString() };
    db.photos.push(row);
    // Only push to Supabase if URL is a real URL
    if (p.url && !p.url.startsWith('data:') && !p.url.startsWith('blob:')) {
      sbPush('photos', 'insert', row);
    }
  });
  saveDB(db);
}

// ══════════════════════════════════════
//  SELFIE
// ══════════════════════════════════════
export function uploadSelfie(profileId, url, selfieCode) {
  const db = getDB();
  const i = db.profiles.findIndex(p => p.id === profileId);
  if (i >= 0) {
    db.profiles[i].selfie_path = url;
    db.profiles[i].selfie_code = selfieCode;
    db.profiles[i].selfie_submitted_at = new Date().toISOString();
    saveDB(db);
    sbPush('profiles', 'update', { selfie_path: url, selfie_code: selfieCode, selfie_submitted_at: new Date().toISOString() }, { id: profileId });
  }
}

// ══════════════════════════════════════
//  DISCOVERY FEED (sync read from localStorage)
// ══════════════════════════════════════
export function getDiscoveryProfiles(userId, seeking, city, ageRange) {
  const db = getDB();
  const interactedIds = new Set(db.interactions.filter(i => i.from_user === userId).map(i => i.to_user));
  const genderFilter = seeking === 'Everyone' ? null : seeking === 'Men' ? 'Man' : 'Woman';
  const jjSenders = new Set(db.interactions.filter(i => i.to_user === userId && i.type === 'jai_jinendra' && !interactedIds.has(i.from_user)).map(i => i.from_user));
  const now = new Date();

  let profiles = db.profiles.filter(p => {
    if (p.id === userId) return false;
    if (interactedIds.has(p.id)) return false;
    if (p.profile_paused === true) return false;
    if (p.selfie_verified !== true || p.report_flag === true || p.admin_blocked === true) return false;
    if (genderFilter && p.gender !== genderFilter) return false;
    if (ageRange && p.dob) {
      const b = new Date(p.dob); let age = now.getFullYear() - b.getFullYear();
      const m = now.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
      if (age < ageRange[0] || age > ageRange[1]) return false;
    }
    return true;
  });

  profiles.sort((a, b) => {
    const aJJ = jjSenders.has(a.id) ? 1 : 0, bJJ = jjSenders.has(b.id) ? 1 : 0;
    if (aJJ !== bJJ) return bJJ - aJJ;
    if (a.city === city && b.city !== city) return -1;
    if (b.city === city && a.city !== city) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return profiles.map(p => ({
    ...p,
    photos: db.photos.filter(ph => ph.profile_id === p.id).sort((a, b) => a.position - b.position),
    hasJaiJinendra: jjSenders.has(p.id),
  }));
}

// ══════════════════════════════════════
//  INTERACTIONS
// ══════════════════════════════════════
export async function getDailyActionCount(userId) {
  if (!userId) return 0;

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).toISOString();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();

  if (SB) {
    try {
      const { count, error } = await supabase
        .from('interactions')
        .select('id', { count: 'exact', head: true })
        .eq('from_user', userId)
        .gte('created_at', start)
        .lte('created_at', end);
      if (!error) return count || 0;
    } catch (e) {
      console.warn('Daily action count query failed:', e);
    }
  }
  return 0;
}

export async function createInteraction(fromUser, toUser, type, promptIndex) {
  const db = getDB();
  if (db.interactions.find(i => i.from_user === fromUser && i.to_user === toUser)) return { isMatch: false };
  const row = { id: generateId(), from_user: fromUser, to_user: toUser, type, prompt_index: promptIndex || null, created_at: new Date().toISOString() };
  db.interactions.push(row);
  sbPush('interactions', 'insert', row);

  let isMatch = false;
  if (type === 'like' || type === 'jai_jinendra') {
    let mutual = db.interactions.find(i => i.from_user === toUser && i.to_user === fromUser && (i.type === 'like' || i.type === 'jai_jinendra'));
    if (!mutual && SB) {
      try {
        const { data } = await supabase
          .from('interactions')
          .select('*')
          .eq('from_user', toUser)
          .eq('to_user', fromUser)
          .in('type', ['like', 'jai_jinendra'])
          .order('created_at', { ascending: false })
          .limit(1);
        if (data?.[0]) {
          mutual = data[0];
          if (!db.interactions.find(i => i.id === mutual.id)) db.interactions.push(mutual);
        }
      } catch (e) {
        console.warn('Mutual interaction lookup failed:', e);
      }
    }

    if (mutual) {
      let existingMatch = db.matches.find(m =>
        (m.user_1 === fromUser && m.user_2 === toUser) ||
        (m.user_1 === toUser && m.user_2 === fromUser)
      );

      if (!existingMatch && SB) {
        try {
          const { data } = await supabase
            .from('matches')
            .select('*')
            .or(`and(user_1.eq.${fromUser},user_2.eq.${toUser}),and(user_1.eq.${toUser},user_2.eq.${fromUser})`)
            .order('created_at', { ascending: false })
            .limit(1);
          if (data?.[0]) {
            existingMatch = data[0];
            if (!db.matches.find(m => m.id === existingMatch.id)) db.matches.push(existingMatch);
          }
        } catch (e) {
          console.warn('Existing match lookup failed:', e);
        }
      }

      if (!existingMatch) {
        const match = { id: generateId(), user_1: fromUser, user_2: toUser, created_at: new Date().toISOString(), date_logged: false, date_outcome: null };
        db.matches.push(match);
        sbPush('matches', 'insert', match);
      }
      isMatch = true;
    }
  }
  if (type === 'jai_jinendra') {
    const notif = { id: generateId(), user_id: toUser, message: 'Someone sent you a Jai Jinendra', type: 'jai_jinendra', read: false, created_at: new Date().toISOString() };
    if (!db.notifications) db.notifications = [];
    db.notifications.push(notif);
  }
  saveDB(db);
  return { isMatch };
}

// ══════════════════════════════════════
//  MATCHES
// ══════════════════════════════════════
export function getMatches(userId) {
  const db = getDB();
  return db.matches.filter(m => m.user_1 === userId || m.user_2 === userId).map(m => {
    const otherId = m.user_1 === userId ? m.user_2 : m.user_1;
    const otherProfile = db.profiles.find(p => p.id === otherId);
    const otherPhotos = db.photos.filter(p => p.profile_id === otherId).sort((a, b) => a.position - b.position);
    const msgs = db.messages.filter(msg => msg.match_id === m.id);
    const interaction = db.interactions.find(i => ((i.from_user === userId && i.to_user === otherId) || (i.from_user === otherId && i.to_user === userId)) && i.prompt_index);
    return { ...m, otherProfile, otherPhotos, lastMessage: msgs[msgs.length - 1] || null, messageCount: msgs.length, promptInteraction: interaction };
  }).sort((a, b) => new Date(b.lastMessage?.created_at || b.created_at) - new Date(a.lastMessage?.created_at || a.created_at));
}
export function getMatch(matchId) { return getDB().matches.find(m => m.id === matchId) || null; }
export function updateMatch(matchId, updates) {
  const db = getDB(); const i = db.matches.findIndex(m => m.id === matchId);
  if (i >= 0) { db.matches[i] = { ...db.matches[i], ...updates }; saveDB(db); sbPush('matches', 'update', updates, { id: matchId }); }
}

export async function exitChat(matchId, currentUserId, otherUserId) {
  try {
    const db = getDB();
    db.messages = db.messages.filter(m => m.match_id !== matchId);
    db.matches = db.matches.filter(m => m.id !== matchId);
    if (db.match_intents) db.match_intents = db.match_intents.filter(mi => mi.match_id !== matchId);
    const now = new Date().toISOString();
    if (!db.interactions.find(i => i.from_user === currentUserId && i.to_user === otherUserId)) {
      db.interactions.push({ id: generateId(), from_user: currentUserId, to_user: otherUserId, type: 'exited', prompt_index: null, created_at: now });
    }
    if (!db.interactions.find(i => i.from_user === otherUserId && i.to_user === currentUserId)) {
      db.interactions.push({ id: generateId(), from_user: otherUserId, to_user: currentUserId, type: 'exited', prompt_index: null, created_at: now });
    }
    saveDB(db);
  } catch (e) {
    return { success: false, error: e.message };
  }

  if (SB) {
    const now = new Date().toISOString();
    const today = now.split('T')[0];
    const safe = async (fn) => { try { await fn(); } catch (_) {} };
    await safe(() => supabase.from('messages').delete().eq('match_id', matchId));
    await safe(() => supabase.from('matches').delete().eq('id', matchId));
    await safe(() => supabase.from('match_intent').delete().eq('match_id', matchId));
    await safe(() => supabase.from('interactions').insert([
      { from_user: currentUserId, to_user: otherUserId, type: 'exited', created_at: now },
      { from_user: otherUserId, to_user: currentUserId, type: 'exited', created_at: now },
    ]));
    await safe(() => supabase.from('profile_views').upsert([
      { viewer_id: currentUserId, viewed_id: otherUserId, viewed_date: today },
      { viewer_id: otherUserId, viewed_id: currentUserId, viewed_date: today },
    ], { onConflict: 'viewer_id,viewed_id', ignoreDuplicates: true }));
  }

  return { success: true };
}

// Count active conversations (last message within 14 days)
export function countActiveConversations(userId) {
  const db = getDB();
  const matches = db.matches.filter(m => m.user_1 === userId || m.user_2 === userId);
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
  
  return matches.filter(m => {
    const msgs = db.messages.filter(msg => msg.match_id === m.id);
    const lastMsg = msgs[msgs.length - 1];
    if (!lastMsg) return false;
    return new Date(lastMsg.created_at) >= fourteenDaysAgo;
  }).length;
}

// ══════════════════════════════════════
//  MESSAGES
// ══════════════════════════════════════
export function getMessages(matchId) { return getDB().messages.filter(m => m.match_id === matchId).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); }
export function sendMessage(matchId, senderId, content, promptContext) {
  const db = getDB();
  const msg = { id: generateId(), match_id: matchId, sender_id: senderId, content, prompt_context: promptContext || null, created_at: new Date().toISOString() };
  db.messages.push(msg); saveDB(db);
  sbPush('messages', 'insert', msg);
}

// ══════════════════════════════════════
//  JAI JINENDRA
// ══════════════════════════════════════
export async function canUseJaiJinendra(userId) {
  if (!SB) return true;
  const { data } = await supabase
    .from('profiles')
    .select('namaskar_used_at')
    .eq('id', userId)
    .maybeSingle();
  if (!data?.namaskar_used_at) return true;
  const now = new Date();
  const day = now.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setUTCDate(now.getUTCDate() + diff);
  monday.setUTCHours(0, 0, 0, 0);
  return new Date(data.namaskar_used_at) < monday;
}
export async function useJaiJinendra(userId) {
  if (!SB) return;
  await supabase.from('profiles').update({ namaskar_used_at: new Date().toISOString() }).eq('id', userId);
  sbPush('jai_jinendra_usage', 'insert', { id: generateId(), user_id: userId, used_at: new Date().toISOString(), week_start: getCurrentWeekKey() });
}

// ══════════════════════════════════════
//  REFERRAL CODES  (referral_codes table)
// ══════════════════════════════════════
function buildRandomCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'JIV-';
  for (let i = 0; i < 6; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
}

export async function generateReferralCode() {
  if (!SB) return { success: false, error: 'Supabase not configured' };
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = buildRandomCode();
    const { data: existing } = await supabase.from('referral_codes').select('id').eq('code', code).limit(1);
    if (existing && existing.length > 0) continue;
    const { error } = await supabase.from('referral_codes').insert({ code, created_at: new Date().toISOString() });
    if (error) {
      console.warn('[generateReferralCode]', error.message);
      return { success: false, error: error.message };
    }
    return { success: true, code };
  }
  return { success: false, error: 'Could not generate unique code after 5 attempts' };
}

export async function validateAndApplyReferralCode(userId, code) {
  if (!SB) return { success: false, error: 'Not connected' };
  const normalized = (code || '').trim().toUpperCase();
  if (!normalized) return { success: false, error: 'Enter a code' };

  // Step 1: Look up the code
  const { data: codeRow, error: lookupErr } = await supabase
    .from('referral_codes')
    .select('*')
    .eq('code', normalized)
    .is('used_by', null)
    .maybeSingle();
  if (lookupErr || !codeRow) return { success: false, error: 'Invalid or already used code' };

  // Step 2: Check profile eligibility
  const { data: prof, error: profErr } = await supabase
    .from('profiles')
    .select('vouch_status')
    .eq('id', userId)
    .maybeSingle();
  if (profErr || !prof) return { success: false, error: 'Profile not found' };
  if (prof.vouch_status === 'verified') return { success: false, error: 'Already verified' };

  // Step 3: Atomic claim — WHERE used_by IS NULL prevents double-use
  const { data: claimed, error: claimErr } = await supabase
    .from('referral_codes')
    .update({ used_by: userId, used_at: new Date().toISOString() })
    .eq('code', normalized)
    .is('used_by', null)
    .select();
  if (claimErr || !claimed || claimed.length === 0) {
    return { success: false, error: 'Code was just used by someone else' };
  }

  // Step 4: Upgrade profile
  const { error: profileUpdateErr } = await supabase
    .from('profiles')
    .update({ vouch_status: 'verified', referred_by: codeRow.id })
    .eq('id', userId);
  if (profileUpdateErr) console.warn('[referral] profile update error:', profileUpdateErr.message);

  // Update localStorage cache so the UI reflects immediately
  const localDb = getDB();
  const idx = localDb.profiles.findIndex(p => p.id === userId);
  if (idx >= 0) {
    localDb.profiles[idx].vouch_status = 'verified';
    localDb.profiles[idx].referred_by = codeRow.id;
    saveDB(localDb);
  }

  // Step 5: Delete the code row so it cannot be reused
  await supabase.from('referral_codes').delete().eq('code', normalized);

  return { success: true };
}

export async function getReferralCodes() {
  if (!SB) return [];
  const { data, error } = await supabase
    .from('referral_codes')
    .select('id, code, created_at, used_by, used_at')
    .order('created_at', { ascending: false });
  if (error) { console.warn('[getReferralCodes]', error.message); return []; }
  return data || [];
}

export async function deleteReferralCode(codeId) {
  if (!SB) return false;
  const { error } = await supabase.from('referral_codes').delete().eq('id', codeId);
  if (error) { console.warn('[deleteReferralCode]', error.message); return false; }
  return true;
}

// ══════════════════════════════════════
//  VOUCHES (Supabase RPC + localStorage cache)
// ══════════════════════════════════════
export async function getVouchApplicants(userId) {
  if (SB) {
    try {
      const { data: applicants, error } = await supabase.from('profiles')
        .select('*')
        .eq('vouch_status', 'member')
        .eq('applied_for_verification', true)
        .eq('admin_blocked', false)
        .eq('report_flag', false)
        .neq('id', userId);
      if (error) throw error;
      if (!applicants || applicants.length === 0) return [];

      // Filter out already-vouched
      const { data: myVouches } = await supabase.from('vouches').select('applicant_id').eq('voucher_id', userId);
      const vouchedSet = new Set((myVouches || []).map(v => v.applicant_id));
      const filtered = applicants.filter(p => !vouchedSet.has(p.id));

      // Fetch photos for filtered applicants
      const ids = filtered.map(p => p.id);
      let photosMap = {};
      if (ids.length > 0) {
        const { data: photos } = await supabase.from('photos').select('*').in('profile_id', ids).order('position');
        (photos || []).forEach(ph => { if (!photosMap[ph.profile_id]) photosMap[ph.profile_id] = []; photosMap[ph.profile_id].push(ph); });
      }
      return filtered.map(p => ({ ...p, photos: photosMap[p.id] || [] }));
    } catch (e) { console.warn('getVouchApplicants error:', e); }
  }
  return [];
}

export async function getMonthlyVouchCount(userId) {
  if (SB) {
    try {
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      const { count, error } = await supabase.from('vouches').select('*', { count: 'exact', head: true }).eq('voucher_id', userId).gte('created_at', monthStart);
      if (!error) return count || 0;
    } catch (_) {}
  }
  return 0;
}

export async function getVouchCount(userId) {
  if (!SB) return 0;
  const { count } = await supabase
    .from('vouches')
    .select('*', { count: 'exact', head: true })
    .eq('applicant_id', userId);
  return count || 0;
}

export async function createVouch(voucherId, applicantId) {
  if (!SB) return false;
  const { data: voucher, error: fetchErr } = await supabase
    .from('profiles')
    .select('selfie_verified, vouch_status')
    .eq('id', voucherId)
    .maybeSingle();
  if (fetchErr || !voucher) return false;
  if (!voucher.selfie_verified) return false;
  if (!['vouched', 'verified'].includes(voucher.vouch_status)) return false;

  const { error } = await supabase
    .from('vouches')
    .upsert(
      { voucher_id: voucherId, applicant_id: applicantId },
      { onConflict: 'voucher_id,applicant_id', ignoreDuplicates: true }
    );
  if (error) { console.warn('[sb:vouches.upsert]', error.message); return false; }
  return true;
}

export async function applyForVerification(userId) {
  if (!SB) return false;
  // Guard: fetch current profile state from DB before updating
  const { data: current, error: fetchErr } = await supabase
    .from('profiles')
    .select('vouch_status, applied_for_verification')
    .eq('id', userId)
    .maybeSingle();
  if (fetchErr || !current) return false;
  if (current.vouch_status !== 'member') return false;
  if (current.applied_for_verification === true) return false;

  const now = new Date().toISOString();
  const { error } = await supabase
    .from('profiles')
    .update({ applied_for_verification: true, applied_at: now })
    .eq('id', userId);
  return !error;
}

// ══════════════════════════════════════
//  MATCH INTENTS
// ══════════════════════════════════════
export async function createMatchIntent(matchId, userId, intent) {
  if (!SB) return false;
  const normalized = intent === 'Open to both' ? 'open' : intent.toLowerCase();
  const { error } = await supabase.from('match_intent')
    .upsert(
      { match_id: matchId, user_id: userId, intent: normalized, responded_at: new Date().toISOString() },
      { onConflict: 'match_id,user_id' }
    );
  if (error) { console.warn('[sb:match_intent.upsert]', error.message); return false; }
  await syncMatchIntents(matchId);
  return true;
}

export function getMatchIntent(matchId, user1Id, user2Id) {
  const db = getDB();
  if (!db.match_intents) return null;
  const intents = db.match_intents.filter(mi => mi.match_id === matchId);
  const intent1 = intents.find(mi => mi.user_id === user1Id)?.intent;
  const intent2 = intents.find(mi => mi.user_id === user2Id)?.intent;
  if (!intent1 || !intent2) return null;
  return { user_1_intent: intent1, user_2_intent: intent2 };
}

export async function hasUserSetIntent(matchId, userId) {
  if (SB) {
    const { data } = await supabase.from('match_intent').select('id').eq('match_id', matchId).eq('user_id', userId).limit(1);
    return !!(data && data.length > 0);
  }
  const db = getDB();
  return !!(db.match_intents?.some(mi => mi.match_id === matchId && mi.user_id === userId));
}

export async function syncMatchIntents(matchId) {
  if (!SB) return;
  try {
    const { data: mis } = await supabase.from('match_intent').select('*').eq('match_id', matchId);
    if (mis) {
      const db = getDB();
      if (!db.match_intents) db.match_intents = [];
      mis.forEach(mi => {
        const i = db.match_intents.findIndex(x => x.match_id === mi.match_id && x.user_id === mi.user_id);
        if (i >= 0) db.match_intents[i] = mi; else db.match_intents.push(mi);
      });
      saveDB(db);
    }
  } catch (_) {}
}

export async function getMatchIntents(matchId) {
  if (SB) {
    try {
      const { data } = await supabase
        .from('match_intent')
        .select('user_id, intent')
        .eq('match_id', matchId);
      if (data) {
        const db = getDB();
        if (!db.match_intents) db.match_intents = [];
        data.forEach(mi => {
          const i = db.match_intents.findIndex(x => x.match_id === matchId && x.user_id === mi.user_id);
          if (i >= 0) db.match_intents[i] = { ...db.match_intents[i], ...mi, match_id: matchId };
          else db.match_intents.push({ ...mi, match_id: matchId });
        });
        saveDB(db);
        return data;
      }
    } catch (e) {
      console.warn('[getMatchIntents]', e);
    }
  }
  const db = getDB();
  return (db.match_intents || [])
    .filter(mi => mi.match_id === matchId)
    .map(mi => ({ user_id: mi.user_id, intent: mi.intent }));
}

// ══════════════════════════════════════
//  NOTIFICATIONS (localStorage only)
// ══════════════════════════════════════
export function getNotifications(userId) { return (getDB().notifications || []).filter(n => n.user_id === userId && !n.read); }
export function markNotificationsRead(userId) {
  const db = getDB(); if (!db.notifications) return;
  db.notifications.forEach(n => { if (n.user_id === userId) n.read = true; }); saveDB(db);
}

// ══════════════════════════════════════
//  REPORTS
// ══════════════════════════════════════
export function createReport(reporterId, reportedId, matchId, reason) {
  const db = getDB();
  const row = { id: generateId(), reporter_id: reporterId, reported_id: reportedId, match_id: matchId, reason, created_at: new Date().toISOString() };
  db.reports.push(row); sbPush('reports', 'insert', row);
  
  // DB trigger handles report_flag auto-setting when distinct reporters reach 2; don't replicate logic here
  // Just insert the report row
  
  saveDB(db);
}

// ══════════════════════════════════════
//  ADMIN (Supabase-first, localStorage fallback)
// ══════════════════════════════════════
export async function getAdminStats() {
  if (SB) {
    try {
      // Fetch profiles (graceful per-query)
      let profiles = [];
      const { data: pData, error: pErr } = await supabase.from('profiles').select('id, city, selfie_verified, vouch_status, created_at');
      if (!pErr && pData) profiles = pData;
      else if (pErr) console.warn('Admin: profiles query failed:', pErr.message);

      // Fetch match count (graceful — table may not exist)
      let matchCount = 0;
      try {
        const { count, error: mErr } = await supabase.from('matches').select('*', { count: 'exact', head: true });
        if (!mErr) matchCount = count || 0;
      } catch (_) { /* matches table may not exist */ }

      const cc = {};
      profiles.forEach(p => { if (p.city) cc[p.city] = (cc[p.city] || 0) + 1; });
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
      return {
        totalUsers: profiles.length,
        verifiedUsers: profiles.filter(p => p.selfie_verified === true && ['vouched', 'verified'].includes(p.vouch_status)).length,
        newSignups: profiles.filter(p => p.created_at && p.created_at > sevenDaysAgo).length,
        totalMatches: matchCount,
        topCities: Object.entries(cc).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([city, count]) => ({ city, count })),
      };
    } catch (e) { console.warn('Admin stats Supabase error, falling back:', e); }
  }
  // Fallback to localStorage
  const db = getDB(); const cc = {};
  db.profiles.forEach(p => { if (p.city) cc[p.city] = (cc[p.city] || 0) + 1; });
  return {
    totalUsers: db.profiles.length,
    verifiedUsers: db.profiles.filter(p => p.selfie_verified === true && ['vouched', 'verified'].includes(p.vouch_status)).length,
    newSignups: db.profiles.filter(p => new Date(p.created_at) > new Date(Date.now() - 7 * 86400000)).length,
    totalMatches: db.matches.length,
    topCities: Object.entries(cc).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([city, count]) => ({ city, count })),
  };
}

export async function getFlaggedProfiles() {
  if (SB) {
    try {
      const { data, error } = await supabase.from('profiles').select('id, display_name, city, sect, report_count, report_flag').eq('report_flag', true);
      if (error) throw error;
      return data || [];
    } catch (e) { console.warn('Flagged profiles Supabase error, falling back:', e); }
  }
  return getDB().profiles.filter(p => p.report_flag === true);
}

export async function restoreProfile(userId) {
  updateProfile(userId, { report_flag: false });
  if (SB) {
    const { error } = await supabase.from('profiles').update({ report_flag: false }).eq('id', userId);
    if (error) console.warn('Restore profile Supabase error:', error);
  }
}

export async function removeProfile(userId) {
  updateProfile(userId, { admin_blocked: true });
  if (SB) {
    const { error } = await supabase.from('profiles').update({ admin_blocked: true }).eq('id', userId);
    if (error) console.warn('Remove profile Supabase error:', error);
  }
}

export async function getReportedProfiles() {
  if (!SB) return [];
  try {
    // Step 1: get every profile that has at least one report row
    const { data: reportRows, error: repErr } = await supabase
      .from('reports')
      .select('reported_id');
    if (repErr) throw repErr;
    if (!reportRows || reportRows.length === 0) return [];

    const profileIds = [...new Set(reportRows.map(r => r.reported_id))];

    // Step 2: fetch those profiles ordered by report_count descending
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('id, display_name, last_initial, city, sect, gender, selfie_verified, vouch_status, report_flag, admin_blocked, report_count')
      .in('id', profileIds)
      .order('report_count', { ascending: false });
    if (error) throw error;
    if (!profiles || profiles.length === 0) return [];

    const { data: reports } = await supabase
      .from('reports')
      .select('id, reported_id, reason, created_at, reporter_id')
      .in('reported_id', profileIds)
      .order('created_at', { ascending: false });

    const reporterIds = [...new Set((reports || []).map(r => r.reporter_id))];
    const reporterNames = {};
    if (reporterIds.length > 0) {
      const { data: reporters } = await supabase
        .from('profiles')
        .select('id, display_name, last_initial')
        .in('id', reporterIds);
      (reporters || []).forEach(r => {
        reporterNames[r.id] = `${r.display_name}${r.last_initial ? ` ${r.last_initial}.` : ''}`;
      });
    }

    const { data: photos } = await supabase
      .from('photos')
      .select('profile_id, url')
      .in('profile_id', profileIds)
      .eq('position', 0);
    const photoMap = {};
    (photos || []).forEach(ph => {
      let url = ph.url;
      if (url && !url.startsWith('http')) {
        url = supabase.storage.from('profile-photos').getPublicUrl(url)?.data?.publicUrl || null;
      }
      photoMap[ph.profile_id] = url;
    });

    return profiles.map(p => ({
      ...p,
      cover_url: photoMap[p.id] || null,
      reports: (reports || [])
        .filter(r => r.reported_id === p.id)
        .map(r => ({ ...r, reporter_name: reporterNames[r.reporter_id] || 'Unknown' })),
    }));
  } catch (e) {
    console.warn('getReportedProfiles error:', e);
    return [];
  }
}

export async function suspendProfile(profileId) {
  if (!SB) return { success: false, error: 'Supabase not configured' };
  const { error } = await supabase
    .from('profiles')
    .update({ report_flag: true, admin_blocked: true })
    .eq('id', profileId);
  if (error) {
    console.error('suspendProfile error:', error);
    return { success: false, error: error.message };
  }
  return { success: true };
}

export async function unsuspendProfile(profileId) {
  if (!SB) return { success: false, error: 'Supabase not configured' };
  const { error } = await supabase
    .from('profiles')
    .update({ report_flag: false, admin_blocked: false, report_count: 0 })
    .eq('id', profileId);
  if (error) {
    console.error('unsuspendProfile error:', error);
    return { success: false, error: error.message };
  }
  return { success: true };
}

function pruneLocalUserData(userId) {
  const db = getDB();
  const ownMatchIds = db.matches
    .filter(m => m.user_1 === userId || m.user_2 === userId)
    .map(m => m.id);

  db.users = db.users.filter(u => u.id !== userId);
  db.profiles = db.profiles.filter(p => p.id !== userId);
  db.photos = db.photos.filter(p => p.profile_id !== userId);
  db.interactions = db.interactions.filter(i => i.from_user !== userId && i.to_user !== userId);
  db.matches = db.matches.filter(m => m.user_1 !== userId && m.user_2 !== userId);
  db.messages = db.messages.filter(m => !ownMatchIds.includes(m.match_id) && m.sender_id !== userId);
  db.reports = db.reports.filter(r => r.reporter_id !== userId && r.reported_id !== userId);
  db.jai_jinendra_usage = db.jai_jinendra_usage.filter(j => j.user_id !== userId);
  db.vouches = db.vouches.filter(v => v.voucher_id !== userId && v.applicant_id !== userId);
  if (Array.isArray(db.notifications)) db.notifications = db.notifications.filter(n => n.user_id !== userId);
  saveDB(db);
}

export async function deleteAccount(userId) {
  if (!userId) {
    resetDB();
    return { success: true, authDeleted: false, failedSteps: [] };
  }

  let authDeleted = false;
  let rpcUnavailable = false;
  const failedSteps = [];
  if (SB) {
    const storageCleanup = await deleteUserPhotosFromStorage(userId);
    if (!storageCleanup.success) {
      failedSteps.push(`storage-profile-photos: ${storageCleanup.error}`);
      console.warn('Account data cleanup step failed (storage-profile-photos):', storageCleanup.error);
    }

    // Prefer server-side deletion RPC when available.
    try {
      const { data, error } = await supabase.rpc('delete_own_account');
      if (!error && data === true) {
        authDeleted = true;
      } else if (error) {
        const msg = error.message || '';
        if (msg.includes('Could not find the function public.delete_own_account')) {
          rpcUnavailable = true;
          console.warn('delete_own_account RPC unavailable; falling back to client-side cleanup.');
        } else {
          failedSteps.push(`delete_own_account: ${msg}`);
          console.warn('Account data cleanup step failed (delete_own_account):', msg);
        }
      }
    } catch (e) {
      const msg = e?.message || 'Unknown error';
      if (msg.includes('Could not find the function public.delete_own_account')) {
        rpcUnavailable = true;
        console.warn('delete_own_account RPC unavailable; falling back to client-side cleanup.');
      } else {
        failedSteps.push(`delete_own_account: ${msg}`);
        console.warn('delete_own_account RPC failed:', e);
      }
    }

    if (!authDeleted) {
    const safeExec = async (name, fn) => {
      try {
        const result = await fn();
        if (result?.error) {
          failedSteps.push(`${name}: ${result.error.message}`);
          console.warn(`Account data cleanup step failed (${name}):`, result.error.message);
          return;
        }
      } catch (e) {
        failedSteps.push(`${name}: ${e?.message || 'Unknown error'}`);
        console.warn(`Account data cleanup step failed (${name}):`, e);
      }
    };

    try {
      const localMatchIds = getDB().matches
        .filter(m => m.user_1 === userId || m.user_2 === userId)
        .map(m => m.id);

      const { data: ownMatches, error: ownMatchesError } = await supabase
        .from('matches')
        .select('id')
        .or(`user_1.eq.${userId},user_2.eq.${userId}`);
      if (ownMatchesError) {
        failedSteps.push(`matches-select: ${ownMatchesError.message}`);
        console.warn('Account data cleanup step failed (matches-select):', ownMatchesError.message);
      }
      const ownMatchIds = Array.from(new Set([...(ownMatches || []).map(m => m.id), ...localMatchIds]));

      if (ownMatchIds.length > 0) {
        await safeExec('messages', () => supabase.from('messages').delete().in('match_id', ownMatchIds));
      }
      await safeExec('messages-sender', () => supabase.from('messages').delete().eq('sender_id', userId));
      await safeExec('photos', () => supabase.from('photos').delete().eq('profile_id', userId));
      await safeExec('interactions', () => supabase.from('interactions').delete().or(`from_user.eq.${userId},to_user.eq.${userId}`));
      await safeExec('reports', () => supabase.from('reports').delete().or(`reporter_id.eq.${userId},reported_id.eq.${userId}`));
      await safeExec('jai_jinendra_usage', () => supabase.from('jai_jinendra_usage').delete().eq('user_id', userId));
      await safeExec('vouches', () => supabase.from('vouches').delete().or(`voucher_id.eq.${userId},applicant_id.eq.${userId}`));
      await safeExec('notifications', () => supabase.from('notifications').delete().eq('user_id', userId));
      await safeExec('matches', () => supabase.from('matches').delete().or(`user_1.eq.${userId},user_2.eq.${userId}`));
      await safeExec('profiles', () => supabase.from('profiles').delete().eq('id', userId));
    } catch (e) {
      console.warn('Account data cleanup failed:', e);
    }
    }
  }

  // Auth-user deletion RPC is optional in environments where the function
  // is not deployed. Data cleanup is considered successful if all other
  // deletion steps succeeded.
  const criticalFailedSteps = failedSteps.filter(step => !step.startsWith('delete_own_account:'));
  const success = criticalFailedSteps.length === 0;

  if (success) {
    pruneLocalUserData(userId);
    resetDB();
  }

  return { success, authDeleted, rpcUnavailable, failedSteps };
}

export function resetDB() { localStorage.removeItem(DB_KEY); localStorage.removeItem(SESSION_KEY); }

// ══════════════════════════════════════
//  AUTO-VERIFICATION (DEPRECATED)
// ══════════════════════════════════════
export function autoVerifyProfile(userId) {
  // DEPRECATED: Admin-only approval now; selfie_verified is set by Admin.js approveSelfie
  // This function is retained for backward compatibility but does nothing
  return false;
}

// ══════════════════════════════════════
//  EK JIV — WEEKLY BEST MATCH
// ══════════════════════════════════════

function getCurrentWeekKey() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const startOfYear = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getUTCDay() + 1) / 7);
  return year + '-W' + String(week).padStart(2, '0');
}

export async function getEkJivMatch(userId, userProfile) {
  if (!SB) return null;
  try {
    const weekKey = getCurrentWeekKey();

    // STEP 1 — Check if selection already exists this week
    const { data: existing } = await supabase
      .from('ek_jiv_selections')
      .select('*')
      .eq('user_id', userId)
      .eq('week_key', weekKey)
      .maybeSingle();

    if (existing) {
      if (existing.acted_on) return null;
      const { data: p } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', existing.matched_profile_id)
        .maybeSingle();
      if (!p) return null;
      const { data: ph } = await supabase
        .from('photos')
        .select('*')
        .eq('profile_id', existing.matched_profile_id)
        .order('position');
      return {
        ...p,
        photos: ph || [],
        cover_url: ph?.[0]?.url || null,
        isEkJiv: true,
        ekJivSeen: existing.seen,
        ekJivActedOn: false,
      };
    }

    // STEP 2 — Build exclude list
    const [{ data: interacted }, { data: viewed }] = await Promise.all([
      supabase.from('interactions').select('to_user').eq('from_user', userId),
      supabase.from('profile_views').select('viewed_id').eq('viewer_id', userId),
    ]);
    const uniqueExclude = [...new Set([
      userId,
      ...((interacted || []).map(r => r.to_user)),
      ...((viewed || []).map(r => r.viewed_id)),
    ])];

    // STEP 3 — Fetch candidate pool
    let query = supabase
      .from('profiles')
      .select('*')
      .eq('selfie_verified', true)
      .eq('profile_paused', false)
      .eq('admin_blocked', false)
      .eq('report_flag', false)
      .neq('id', userId)
      .limit(100);

    query = query.not('id', 'in', `(${uniqueExclude.join(',')})`);

    if (userProfile.seeking === 'Men') query = query.eq('gender', 'Man');
    else if (userProfile.seeking === 'Women') query = query.eq('gender', 'Woman');

    const { data: candidates } = await query;
    if (!candidates || candidates.length === 0) return null;

    // STEP 4 — Score each candidate
    const calcAge = (dob) => {
      if (!dob) return null;
      const b = new Date(dob);
      const now = new Date();
      let age = now.getFullYear() - b.getFullYear();
      const m = now.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
      return age;
    };
    const userAge = calcAge(userProfile.dob);

    const scored = candidates.map(c => {
      let score = 0;
      if (c.city && c.city === userProfile.city) score += 4;
      if (userAge !== null && c.dob) {
        const age = calcAge(c.dob);
        if (age !== null && Math.abs(age - userAge) <= 3) score += 3;
      }
      if (c.sect && c.sect === userProfile.sect) score += 3;
      if (c.selfie_verified === true) score += 2;
      if (c.vouch_status === 'vouched' || c.vouch_status === 'verified') score += 2;
      const myTraits = userProfile.personality_traits || [];
      const cTraits = c.personality_traits || [];
      if (myTraits.length > 0 && cTraits.length > 0 && cTraits.some(t => myTraits.includes(t))) score += 2;
      const myHobbies = userProfile.hobbies_list || [];
      const cHobbies = c.hobbies_list || [];
      if (myHobbies.length > 0 && cHobbies.length > 0 && cHobbies.some(h => myHobbies.includes(h))) score += 2;
      if (c.has_jain_prompt === true) score += 1;
      if (c.practice_level && c.practice_level === userProfile.practice_level) score += 1;
      score += Math.random() * 0.5;
      return { candidate: c, score };
    });
    scored.sort((a, b) => b.score - a.score);
    const best = scored[0].candidate;

    // Fetch all photos for the winner
    const { data: ph } = await supabase
      .from('photos')
      .select('*')
      .eq('profile_id', best.id)
      .order('position');

    // STEP 5 — Store selection
    await supabase.from('ek_jiv_selections').upsert(
      {
        user_id: userId,
        matched_profile_id: best.id,
        week_key: weekKey,
        seen: false,
        acted_on: false,
      },
      { onConflict: 'user_id,week_key' }
    );

    // STEP 6 — Return
    return {
      ...best,
      photos: ph || [],
      cover_url: ph?.[0]?.url || null,
      isEkJiv: true,
      ekJivSeen: false,
      ekJivActedOn: false,
    };
  } catch (e) {
    console.warn('getEkJivMatch error:', e);
    return null;
  }
}

export async function markEkJivSeen(userId) {
  if (!SB) return;
  await supabase
    .from('ek_jiv_selections')
    .update({ seen: true })
    .eq('user_id', userId)
    .eq('week_key', getCurrentWeekKey());
}

export async function markEkJivActedOn(userId) {
  if (!SB) return;
  await supabase
    .from('ek_jiv_selections')
    .update({ acted_on: true, seen: true })
    .eq('user_id', userId)
    .eq('week_key', getCurrentWeekKey());
}

export async function createQuizSession(matchId, initiatedBy, quizType) {
  if (!SB) return null;
  const { data, error } = await supabase
    .from('quiz_sessions')
    .insert({ match_id: matchId, initiated_by: initiatedBy, quiz_type: quizType, status: 'waiting' })
    .select()
    .single();
  if (error) { console.warn('createQuizSession error:', error); return null; }
  return data;
}

export async function getQuizSession(matchId) {
  if (!SB) return null;
  const { data, error } = await supabase
    .from('quiz_sessions')
    .select('*')
    .eq('match_id', matchId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) { console.warn('getQuizSession error:', error); return null; }
  return data;
}

export async function acceptQuizSession(sessionId) {
  if (!SB) return { success: false };
  const { error } = await supabase
    .from('quiz_sessions')
    .update({ status: 'active' })
    .eq('id', sessionId);
  if (error) { console.warn('acceptQuizSession error:', error); return { success: false }; }
  return { success: true };
}

export async function saveQuizResponse(sessionId, userId, questionKey, answerIndex) {
  if (!SB) return { success: false };
  const { error } = await supabase
    .from('quiz_responses')
    .upsert(
      { session_id: sessionId, user_id: userId, question_key: questionKey, answer_index: answerIndex },
      { onConflict: 'session_id,user_id,question_key' }
    );
  if (error) { console.warn('saveQuizResponse error:', error); return { success: false }; }
  return { success: true };
}

export async function getQuizResponses(sessionId) {
  if (!SB) return [];
  const { data, error } = await supabase
    .from('quiz_responses')
    .select('user_id, question_key, answer_index, answered_at')
    .eq('session_id', sessionId);
  if (error) { console.warn('getQuizResponses error:', error); return []; }
  return data || [];
}

export async function completeQuizSession(sessionId) {
  if (!SB) return { success: false };
  const { error } = await supabase
    .from('quiz_sessions')
    .update({ status: 'completed', completed_at: new Date().toISOString() })
    .eq('id', sessionId);
  if (error) { console.warn('completeQuizSession error:', error); return { success: false }; }
  return { success: true };
}

export function calculateCompatibilityPercent(responses, myUserId, theirUserId, totalQuestions) {
  const myAnswers = {};
  const theirAnswers = {};
  for (const r of responses) {
    if (r.user_id === myUserId) myAnswers[r.question_key] = r.answer_index;
    else if (r.user_id === theirUserId) theirAnswers[r.question_key] = r.answer_index;
  }
  let alignedCount = 0;
  for (const key of Object.keys(myAnswers)) {
    if (theirAnswers[key] !== undefined && myAnswers[key] === theirAnswers[key]) alignedCount++;
  }
  return Math.round((alignedCount / totalQuestions) * 100);
}

